'use client';

import { useTranslations } from 'next-intl';
import {
  resolveAiComponentDisplayDescription,
  type AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import {
  AI_COMPONENT_TYPE_KEYS,
  getAiComponentTypeIcon,
  resolveAiComponentVerificationBadge,
  type AiComponentVerificationBadgeKey,
} from '@terra/ui-ai-applications';
import { Badge, BodyDefault } from '@terra/ui-design-system';
import { useDateFormatter } from '../../../hooks/useDateFormatter';
import {
  ListPageDetectionMetric,
  type ListPageDetectionType,
} from '../../common/ListPageDetectionMetric';
import { ListPageEmptyCell } from '../../common/ListPageEmptyCell';
import { ListPageNameCell } from '../../common/ListPageNameCell';
import { AssetRowIcon } from '../surface-assets/AssetRowIcon';

const VERIFICATION_BADGE_PALETTE: Record<
  AiComponentVerificationBadgeKey,
  'blue' | 'green' | 'orange' | 'gray'
> = {
  code: 'blue',
  verified: 'green',
  contradicted: 'orange',
  suppressed: 'gray',
  unverified: 'gray',
};

export function AiComponentNameCell({
  component,
}: {
  component: AiComponentDto;
}) {
  return (
    <ListPageNameCell
      icon={<AssetRowIcon icon={getAiComponentTypeIcon(component.type)} />}
      title={component.name}
      subtitle={resolveAiComponentDisplayDescription({
        description: component.description,
        metadata: component.metadata,
        type: component.type,
      })}
    />
  );
}

export function AiComponentTypeBadge({
  type,
}: {
  type: AiComponentDto['type'];
}) {
  const tComponentTypes = useTranslations('aiApplications.componentTypes');

  return (
    <Badge variant="outline" size="sm" colorPalette="gray">
      {tComponentTypes(AI_COMPONENT_TYPE_KEYS[type])}
    </Badge>
  );
}

export function AiComponentTextCell({ value }: { value: string | null }) {
  return value ? (
    <BodyDefault color="fg.neutral.muted" truncate title={value}>
      {value}
    </BodyDefault>
  ) : (
    <ListPageEmptyCell />
  );
}

export function AiComponentCoverageBadge({
  isInternal,
}: {
  isInternal: boolean;
}) {
  const tAssets = useTranslations('aiApplications.assets');

  return (
    <Badge
      variant="outline"
      size="sm"
      colorPalette={isInternal ? 'blue' : 'orange'}
    >
      {isInternal ? tAssets('internal') : tAssets('external')}
    </Badge>
  );
}

export function AiComponentVerificationBadge({
  component,
}: {
  component: AiComponentDto;
}) {
  const tVerification = useTranslations('aiApplications.assets.verification');
  const badge = resolveAiComponentVerificationBadge({ component });

  return (
    <Badge
      variant="outline"
      size="sm"
      colorPalette={VERIFICATION_BADGE_PALETTE[badge]}
    >
      {tVerification(badge)}
    </Badge>
  );
}

export function AiComponentDetectionCell({
  type,
  count,
  isUnavailable,
}: {
  type: ListPageDetectionType;
  count: number;
  isUnavailable: boolean;
}) {
  const tTable = useTranslations('proxy.assets.table');

  return isUnavailable ? (
    <ListPageEmptyCell />
  ) : (
    <ListPageDetectionMetric
      type={type}
      count={count}
      label={
        type === 'signal'
          ? tTable('signalsCount', { count })
          : tTable('findingsCount', { count })
      }
    />
  );
}

export function AiComponentDateCell({ value }: { value: Date | string }) {
  const dateFormatter = useDateFormatter();

  return (
    <BodyDefault color="fg.neutral.muted" truncate>
      {dateFormatter.format(new Date(value))}
    </BodyDefault>
  );
}
